/**
 * Free towel promo: rotates the towel photos and lets the customer close the corner box.
 * Closing hides it on the current page only — it shows again on the next page view.
 * When the corner box would cover the product photos, it moves into the page right under
 * the main product photo (slots marked with data-towel-promo-slot).
 */

if (!customElements.get('towel-promo')) {
  const TOWEL_PROMO_INTERVAL = 3500;
  const TOWEL_PROMO_GAP = 8;

  class TowelPromo extends HTMLElement {
    connectedCallback() {
      // Moving the box between slots reconnects it — set it up only once.
      if (this.ready) return;
      this.ready = true;

      this.querySelector('[data-towel-promo-close]')?.addEventListener('click', () => this.close());

      this.slides = [...this.querySelectorAll('.towel-promo__slide')];
      this.current = 0;
      if (this.slides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.timer = setInterval(() => this.next(), TOWEL_PROMO_INTERVAL);
      }

      if (this.dataset.placement !== 'corner') return;
      this.home = document.createComment('towel-promo');
      this.before(this.home);
      this.place();
      let frame;
      window.addEventListener('resize', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.place());
      });
      window.addEventListener('load', () => this.place());
    }

    // Keeps the box in the corner unless it covers the product photos.
    place() {
      const slot = [...document.querySelectorAll('[data-towel-promo-slot]')].find((el) => el.getClientRects().length);

      // Measure the box where it would sit in the corner.
      this.classList.remove('towel-promo--inline');
      const box = this.getBoundingClientRect();
      const media = slot?.getBoundingClientRect();

      if (!slot || this.hidden || box.right + TOWEL_PROMO_GAP <= media.left || box.left >= media.right) {
        if (this.previousSibling !== this.home) this.home.after(this);
        return;
      }

      this.classList.add('towel-promo--inline');
      if (slot.dataset.towelPromoSlot === 'grid') {
        if (this.parentElement !== slot) slot.append(this);
        return;
      }
      // A sticky photo column on phones would keep the box on screen — put it after the column.
      const target = getComputedStyle(slot.parentElement).position === 'sticky' ? slot.parentElement : slot;
      if (this.previousElementSibling !== target) target.after(this);
    }

    next() {
      const index = (this.current + 1) % this.slides.length;
      const slide = this.slides[index];

      // Wait for the photo to load, otherwise the box would flash empty.
      if (!slide.complete || !slide.naturalWidth) {
        slide.loading = 'eager';
        return;
      }

      // The old photo stays under the new one while it fades in, so the box never goes blank.
      this.slides.forEach((s) => s.classList.remove('is-previous'));
      this.slides[this.current].classList.replace('is-active', 'is-previous');
      slide.classList.add('is-active');
      this.current = index;
    }

    close() {
      clearInterval(this.timer);
      this.hidden = true;
    }
  }

  customElements.define('towel-promo', TowelPromo);
}
